import { USDRateData } from '../types/rateTypes';

/**
 * Calculates conversion rates for any base currency to IRR and other currencies
 * Uses the simplified USD cash rate from Mesghal
 */
export const calculateConversionRates = (
    baseCurrency: string,
    usdRates: { [currency: string]: number },
    usdCashRate: USDRateData
): { [currency: string]: number } => {
    const conversionRates: { [currency: string]: number } = {};

    // Log for debugging
    console.log('💱 calculateConversionRates:', {
        baseCurrency,
        usdToIRR: usdCashRate.usd_cash_rate,
        usdRatesSample: { CAD: usdRates['CAD'], EUR: usdRates['EUR'], GBP: usdRates['GBP'] }
    });

    if (baseCurrency === 'IRR') {
        // Base currency is IRR
        conversionRates['USD'] = 1 / usdCashRate.usd_cash_rate; // IRR to USD

        // Calculate other currencies through USD
        Object.entries(usdRates).forEach(([currency, rate]) => {
            if (currency !== 'USD' && currency !== 'IRR') {
                conversionRates[currency] = (1 / usdCashRate.usd_cash_rate) * rate;
            }
        });

        console.log('✅ IRR base conversions:', {
            'IRR→USD': conversionRates['USD'],
            'IRR→CAD': conversionRates['CAD'],
            'IRR→EUR': conversionRates['EUR']
        });
    } else {
        // Base currency is not IRR
        const baseToUsdRate = usdRates[baseCurrency];
        if (baseToUsdRate) {
            // Calculate IRR rate through USD
            // usdRates[baseCurrency] = how many of baseCurrency per 1 USD
            // usdCashRate.usd_cash_rate = how many IRR per 1 USD
            // So: 1 baseCurrency = (1 USD / baseToUsdRate) * usdCashRate.usd_cash_rate IRR
            // Simplified: 1 baseCurrency = usdCashRate.usd_cash_rate / baseToUsdRate IRR
            // 
            // Example: 1 CAD = ? IRR
            // - usdRates['CAD'] = 1.35 (1 USD = 1.35 CAD, so 1 CAD = 1/1.35 USD = 0.74 USD)
            // - usdCashRate = 800,000 (1 USD = 800,000 IRR)
            // - 1 CAD = 0.74 USD * 800,000 IRR/USD = 592,000 IRR
            // - Formula: usdCashRate.usd_cash_rate / baseToUsdRate = 800,000 / 1.35 = 592,593 IRR ✓
            conversionRates['IRR'] = usdCashRate.usd_cash_rate / baseToUsdRate;
            conversionRates['USD'] = 1 / baseToUsdRate;

            // Calculate other currency rates
            Object.entries(usdRates).forEach(([currency, rate]) => {
                if (currency !== baseCurrency && currency !== 'USD' && currency !== 'IRR') {
                    conversionRates[currency] = rate / baseToUsdRate;
                }
            });

            console.log(`✅ ${baseCurrency} base conversions:`, {
                [`${baseCurrency}→IRR`]: conversionRates['IRR'],
                [`${baseCurrency}→USD`]: conversionRates['USD'],
                calculation: `${usdCashRate.usd_cash_rate} / ${baseToUsdRate} = ${conversionRates['IRR']}`
            });
        }
    }

    return conversionRates;
};

/**
 * Formats currency rates for display
 */
export const formatCurrencyRate = (rate: number, currency: string): string => {
    if (currency === 'IRR') {
        // IRR rates are large numbers, show as whole numbers
        return Math.round(rate).toLocaleString();
    } else {
        // Other currencies show 4 decimal places
        return rate.toFixed(4);
    }
};

/**
 * Validates if conversion rates are reasonable
 */
export const validateConversionRates = (rates: { [currency: string]: number }): boolean => {
    const requiredCurrencies = ['USD', 'IRR'];

    return requiredCurrencies.every(currency => {
        const rate = rates[currency];
        return rate !== undefined && rate > 0 && isFinite(rate);
    });
};

/**
 * Converts amount from one currency to another
 */
export const convertAmount = (
    amount: number,
    fromCurrency: string,
    toCurrency: string,
    rates: { [currency: string]: number }
): number => {
    if (fromCurrency === toCurrency) {
        return amount;
    }

    const rate = rates[toCurrency];
    if (!rate) {
        throw new Error(`Exchange rate not available for ${toCurrency}`);
    }

    return amount * rate;
};

/**
 * Gets the best rate from multiple providers
 */
export const getBestRate = (
    rates: { [provider: string]: { usd_cash_rate: number; source: string } }
): { rate: number; source: string } => {
    const validRates = Object.entries(rates)
        .filter(([_, data]) => data && data.usd_cash_rate > 0)
        .map(([provider, data]) => ({
            provider,
            rate: data.usd_cash_rate,
            source: data.source
        }));

    if (validRates.length === 0) {
        throw new Error('No valid rates available');
    }

    // Sort by rate (ascending for USD/IRR rates)
    validRates.sort((a, b) => a.rate - b.rate);

    // Return the median rate for stability
    const medianIndex = Math.floor(validRates.length / 2);
    const bestRate = validRates[medianIndex];

    return {
        rate: bestRate.rate,
        source: bestRate.source
    };
};

/**
 * Calculates percentage change between two rates
 */
export const calculateRateChange = (
    currentRate: number,
    previousRate: number
): { percentage: number; direction: 'up' | 'down' | 'stable' } => {
    if (previousRate === 0) {
        return { percentage: 0, direction: 'stable' };
    }

    const change = ((currentRate - previousRate) / previousRate) * 100;
    const direction = change > 0.01 ? 'up' : change < -0.01 ? 'down' : 'stable';

    return {
        percentage: Math.abs(change),
        direction
    };
};

/**
 * Formats rate change for display
 */
export const formatRateChange = (change: { percentage: number; direction: 'up' | 'down' | 'stable' }): string => {
    if (change.direction === 'stable') {
        return 'No change';
    }

    const arrow = change.direction === 'up' ? '↗️' : '↘️';
    return `${arrow} ${change.percentage.toFixed(2)}%`;
}; 